import { DialogLayout } from '@/ui';
import { Button } from '@/ui/primitives';
import type { KeyboardEvent, ReactNode } from 'react';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  confirmColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'neutral'
    | 'green'
    | 'red'
    | 'error'
    | 'orange'
    | 'brown'
    | 'blue'
    | 'purple';
  confirmVariant?: 'contained' | 'outlined' | 'text';
  confirmDisabled?: boolean;
  loading?: boolean;
  hideCancel?: boolean;
  disableClose?: boolean;
  allowTopBarInteraction?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const ConfirmDialog = ({
  open,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  confirmColor = 'red',
  confirmVariant = 'outlined',
  confirmDisabled = false,
  loading = false,
  hideCancel = false,
  disableClose = false,
  allowTopBarInteraction = false,
  onClose,
  onConfirm,
}: ConfirmDialogProps): ReactNode => {
  const resolvedVariant =
    confirmVariant === 'outlined' ? 'secondary' : confirmVariant === 'text' ? 'ghost' : 'primary';
  const confirmIsCritical = confirmColor === 'error' || confirmColor === 'red';
  const confirmButtonVariant =
    confirmIsCritical && confirmVariant === 'outlined'
      ? 'destructive'
      : confirmIsCritical && resolvedVariant === 'primary'
        ? 'destructive'
        : resolvedVariant;
  const confirmButtonColor =
    confirmIsCritical && resolvedVariant === 'primary' ? undefined : confirmColor;
  const handleContentKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (confirmIsCritical || loading || confirmDisabled) return;
    if (event.defaultPrevented || event.key !== 'Enter') return;
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName;
    if (tagName === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    onConfirm();
  };
  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      disableClose={disableClose}
      allowTopBarInteraction={allowTopBarInteraction}
      title={title}
      description={description}
      onContentKeyDownCapture={handleContentKeyDownCapture}
      actions={
        <>
          {hideCancel ? null : (
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              aria-label="Cancel confirmation"
            >
              Cancel
            </Button>
          )}
          <Button
            variant={confirmButtonVariant}
            color={confirmButtonColor}
            loading={loading}
            onClick={onConfirm}
            disabled={confirmDisabled}
            aria-label={confirmLabel}
          >
            {confirmLabel}
          </Button>
        </>
      }
      actionsAlign="flex-end"
      actionsGap="md"
    >
      {children ?? null}
    </DialogLayout>
  );
};
